import React, { useEffect, useRef, useState } from 'react';
import { Container } from '../ui/Container';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { useBrand } from '../../Utils/BrandContext';

// ─── Theme colors from tailwind.config.js + BrandSync.js ───
// Blue:   #15304a  (darkBlue)
// Orange: #e88421  (accent)
// Green:  #0b5d1e  (qikerp primary)

const CARDS = [
  {
    id: 'import-data',
    step: '1',
    title: 'Quick Data Import',
    subtitle: 'Start in minutes, not weeks',
    description: 'Import your leads, customers, ledgers, inventory items and more using our simple templates.',
    accentColor: '#0b5d1e',
    accentBg: '#0b5d1e08',
    stepBg: '#0b5d1e',
    iconPath: (
      // Excel → Software SVG illustration (inline, no external dep)
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Spreadsheet */}
        <rect x="8" y="20" width="80" height="100" rx="6" fill="#e8f5e9" stroke="#0b5d1e" strokeWidth="2" />
        <rect x="8" y="20" width="80" height="20" rx="6" fill="#0b5d1e" />
        <rect x="8" y="34" width="80" height="6" fill="#0b5d1e" />
        {[0, 1, 2, 3, 4].map(i => (
          <React.Fragment key={i}>
            <rect x="14" y={48 + i * 14} width="30" height="7" rx="2" fill="#a5d6a7" opacity="0.8" />
            <rect x="50" y={48 + i * 14} width="30" height="7" rx="2" fill="#c8e6c9" opacity="0.6" />
          </React.Fragment>
        ))}
        {/* Arrow flow */}
        <path d="M92 70 Q112 55 112 80 Q112 105 132 90" stroke="#e88421" strokeWidth="3" strokeDasharray="5 3" fill="none" markerEnd="url(#arrow-g)" />
        <defs>
          <marker id="arrow-g" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill="#e88421" />
          </marker>
        </defs>
        {/* Dashboard */}
        <rect x="120" y="30" width="72" height="100" rx="6" fill="#f1f8e9" stroke="#0b5d1e" strokeWidth="2" />
        <rect x="120" y="30" width="72" height="18" rx="6" fill="#15304a" />
        <rect x="128" y="56" width="55" height="8" rx="3" fill="#0b5d1e" opacity="0.7" />
        <rect x="128" y="70" width="40" height="6" rx="3" fill="#0b5d1e" opacity="0.4" />
        <rect x="128" y="82" width="50" height="6" rx="3" fill="#0b5d1e" opacity="0.4" />
        <rect x="128" y="94" width="45" height="6" rx="3" fill="#0b5d1e" opacity="0.4" />
        {/* Check marks */}
        <circle cx="170" cy="113" r="10" fill="#0b5d1e" />
        <path d="M165 113 L169 117 L176 109" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Person */}
        <circle cx="56" cy="140" r="9" fill="#15304a" />
        <path d="M42 158 Q56 148 70 158" stroke="#15304a" strokeWidth="3" strokeLinecap="round" fill="none" />
      </svg>
    ),
    points: [],
  },
  {
    id: 'training',
    step: '2',
    title: 'Interactive Live Training',
    subtitle: 'Guided by experts on Day 1',
    description: 'Join live training sessions where you can ask questions and get your doubts resolved instantly.',
    accentColor: '#e88421',
    accentBg: '#fff7ed',
    stepBg: '#e88421',
    iconPath: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <rect x="30" y="30" width="140" height="90" rx="8" fill="#f8fafc" stroke="#e0e0e0" strokeWidth="2" />
        <rect x="30" y="30" width="140" height="20" rx="8" fill="#e88421" />
        <circle cx="42" cy="40" r="3" fill="white" opacity="0.5" />
        <circle cx="52" cy="40" r="3" fill="white" opacity="0.5" />

        <rect x="40" y="60" width="45" height="45" rx="4" fill="#15304a" opacity="0.05" />
        <rect x="95" y="60" width="65" height="8" rx="4" fill="#0b5d1e" opacity="0.1" />
        <rect x="95" y="75" width="50" height="8" rx="4" fill="#0b5d1e" opacity="0.1" />

        <g filter="drop-shadow(0 4px 4px rgba(0,0,0,0.1))">
          <circle cx="145" cy="95" r="28" fill="white" stroke="#e88421" strokeWidth="2" />
          <path d="M130 112 Q145 102 160 112" stroke="#15304a" strokeWidth="3" strokeLinecap="round" />
          <circle cx="145" cy="90" r="10" fill="#15304a" />
        </g>
      </svg>
    ),
    points: [],
  },
  {
    id: 'free-support',
    step: '3',
    title: 'Support All Week',
    subtitle: "We're here every step of the way",
    description: 'Get free support throughout the week — even on Sundays.',
    accentColor: '#15304a',
    accentBg: '#e8eef4',
    stepBg: '#15304a',
    iconPath: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">

        {/* Background card — identical to card 2 */}
        <rect x="30" y="25" width="140" height="100" rx="8" fill="#f8fafc" stroke="#e0e0e0" strokeWidth="2" />
        <rect x="30" y="25" width="140" height="20" rx="8" fill="#15304a" />
        <circle cx="42" cy="35" r="3" fill="white" opacity="0.4" />
        <circle cx="52" cy="35" r="3" fill="white" opacity="0.4" />

        {/* Support image — centered */}
        <image href="/QikERP/Images/Icons/Support Ticketing.png" x="70" y="37" width="70" height="90" />

      </svg>
    ),
    points: [],
  },
];
const GoLiveSection = () => {
  const [visibleCards, setVisibleCards] = useState(new Set());
  const cardRefs = useRef([]);
  const isMobile = useIsMobile();

  const { brand: activeBrand } = useBrand()


  useEffect(() => {
    const observers = [];
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => new Set([...prev, i]));
            }, i * 120);
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <section
      id="go-live-section"
      className={` border-top py-12 md:py-16 lg:py-20 text-center  ${isMobile ? 'bg-background-light' : 'bg-white'}`}
      style={{ borderTop: '1px solid #e0e0e0' }}
    >
      <style>{`
        @keyframes glSlideUp {
          from { opacity: 0; transform: translateY(36px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .gl-card-visible {
          animation: glSlideUp 0.55s cubic-bezier(.22,.68,0,1.2) both;
        }
        .gl-card-hidden {
          opacity: 0;
          transform: translateY(36px);
        }
        .gl-point::before {
          content: '✓';
          margin-right: 8px;
          font-weight: 700;
        }
        .gl-step-badge {
          width: 36px; height: 36px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; color: white;
          flex-shrink: 0;
        }
        .gl-card {
          background: white;
          border: 1.5px solid #e0e0e0;
          border-radius: 5px;
          overflow: hidden;
          transition: box-shadow 0.25s, border-color 0.25s, transform 0.25s;
          display: flex;
          flex-direction: column;
        }
        .gl-title-bar {
          height: 5px;
          width: 100%;
        }
        .gl-illustration {
          width: 100%;
          height: 170px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px 24px 0;
        }
        .gl-badge-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 24px 0;
        }
        .gl-card-body {
          padding: 15px 24px 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .gl-subtitle {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.06em;
          margin-bottom: 4px;
        }
        .gl-card-title {
          font-size: 20px;
          color: #1a1a1a;
          line-height: 1.25;
        }
        .gl-desc {
          font-size: 16px;
          line-height: 1.65;
          margin-bottom: 0px;
        }
        .gl-points-list {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: auto;
        }
        .gl-point {
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0;
        }
        @media (max-width: 767px) {
          .gl-illustration { height: 140px; }
          .gl-card-title { font-size: 18px; }
        }
      `}</style>

      <Container >
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-14">

          <h2 className="text-[30px] md:text-[40px] font-extrabold text-text-primary leading-tight mb-3">
            Go Live in{' '}
            <span style={{

            }}>
              1 Day
            </span>
          </h2>
          {isMobile ?
            <div className="text-[14px] md:text-lg text-[#808080] px-2">
              <span> Set up your business and start working in just one day with {activeBrand == 'bz' ? 'Biziverse' : 'QikERP'}.</span>
            </div> :
            <div className="text-[14px] md:text-lg text-[#808080] px-2">
              <span> Set up your business and start working in just one day with {activeBrand == 'bz' ? 'Biziverse' : 'QikERP'}.</span>
            </div>}

          {/* Divider accent
          <div className="flex items-center justify-center gap-3 mt-6">
            <div style={{ width: 32, height: 3, borderRadius: 2, background: '#0b5d1e' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#e88421' }} />
            <div style={{ width: 32, height: 3, borderRadius: 2, background: '#15304a' }} />
          </div> */}
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 md:mx-8" style={{ gap: isMobile ? '1rem' : '4rem' }}>
          {CARDS.map((card, i) => (
            <div
              key={card.id}
              ref={el => (cardRefs.current[i] = el)}
              className={`gl-card ${visibleCards.has(i) ? 'gl-card-visible' : 'gl-card-hidden'}`}
              style={{ '--accent': card.accentColor, background: isMobile ? 'white' : '#f8f9fa' }}
            >
              {/* Top color bar */}
              <div className="gl-title-bar" style={{ background: `linear-gradient(90deg, ${card.accentColor}, ${card.accentColor}88)` }} />

              {/* Illustration */}
              <div className="gl-illustration" style={{ background: card.accentBg }}>
                {card.iconPath}
              </div>

              {/* Step badge + subtitle */}
              <div className="gl-badge-row flex !justify-center border-top">
                <div className="gl-step-badge" style={{ background: card.stepBg }}>
                  {card.step}
                </div>
                <span className="gl-card-title font-bold">
                  {card.title}
                </span>
              </div>

              {/* Body */}
              <div className="gl-card-body">
                <p className="gl-desc text-text-secondary mb-0">{card.description}</p>

                {/* <ul className="gl-points-list">
                  {card.points.map((pt, j) => (
                    <li key={j} className="gl-point" style={{ color: card.accentColor }}>

                      <span style={{ color: '#333', fontWeight: 500 }}>{pt}</span>
                    </li>
                  ))}
                </ul> */}
              </div>
            </div>
          ))}
        </div>


      </Container>
    </section>
  );
};

export default GoLiveSection;
